import { Link } from 'react-router-dom';
import {
  HelpCircle,
  MessageSquare,
  ArrowRight,
  ChevronRight,
  Loader2,
  Info,
  Layers,
  Crown,
  User,
  Shield,
  Wrench,
  Baby,
  Heart,
  Calendar,
  CreditCard,
  Bell,
  Users,
  Settings,
  BookOpen,
  type LucideIcon
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { FAQSearchBar } from '../components/FAQSearchBar';
import { useFAQCategories } from '../hooks/useFAQ';

const iconMap: Record<string, LucideIcon> = {
  'info': Info,
  'layers': Layers,
  'crown': Crown,
  'user': User,
  'shield': Shield,
  'wrench': Wrench,
  'help-circle': HelpCircle,
  'baby': Baby,
  'heart': Heart,
  'calendar': Calendar,
  'credit-card': CreditCard,
  'bell': Bell,
  'users': Users,
  'settings': Settings,
  'book-open': BookOpen,
  'message-square': MessageSquare,
};

export function HelpPage() {
  const { data: categories, isLoading, error } = useFAQCategories();
  const { t } = useTranslation('help');

  return (
    <>
      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-6">{t('pageTitle')}</h1>

      {/* Search Bar */}
      <div className="mb-6">
        <FAQSearchBar />
      </div>

      {/* Categories */}
      <div className="space-y-3 mb-8">
        <h2 className="text-sm font-medium text-white/70 mb-3">{t('topics')}</h2>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-white/50" />
          </div>
        ) : error ? (
          <div className="glass-effect rounded-[15px] p-4 text-center">
            <p className="text-white/60">
              {t('errors.loadFailed')}
            </p>
          </div>
        ) : categories && categories.length > 0 ? (
          <div className="space-y-3">
            {categories.map((category) => {
              const Icon = category.icon ? iconMap[category.icon] || HelpCircle : HelpCircle;
              return (
                <Link
                  key={category.id}
                  to={`/help/category/${category.slug}`}
                  className="glass-effect rounded-[15px] p-4 flex items-center gap-4 hover:bg-white/10 transition-colors block"
                >
                  <div className="w-12 h-12 bg-pp-orange/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-pp-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-white">{category.name}</h3>
                    {category.description && (
                      <p className="text-sm text-white/60 truncate">{category.description}</p>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/40 flex-shrink-0" />
                </Link>
              );
            })}
          </div>
        ) : (
          <div className="glass-effect rounded-[15px] p-4 text-center">
            <p className="text-white/60">
              {t('emptyState')}
            </p>
          </div>
        )}
      </div>

      {/* Contact Hint */}
      <div className="glass-effect rounded-[15px] p-4">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-10 h-10 bg-pp-teal/20 rounded-full flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-pp-teal" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-white mb-1">
              {t('contactHint.title')}
            </h3>
            <p className="text-sm text-white/60 mb-3">
              {t('contactHint.description')}
            </p>
            <Link
              to="/help/contact"
              className="inline-flex items-center gap-2 text-sm font-medium text-pp-orange hover:text-pp-orange/80 transition-colors"
            >
              {t('contactHint.link')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

    </>
  );
}
